import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Custom hook for A/B testing
 * Fetches a variant, tracks impressions, and provides conversion tracking
 */
export const useABTest = (page, element) => {
  const [variant, setVariant] = useState(null);
  const [testId, setTestId] = useState(null);
  const [variantId, setVariantId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch variant on mount
  useEffect(() => {
    const fetchVariant = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/ab-test/variant?page=${page}&element=${element}`
        );
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.variant_id) {
            setVariant({
              headline: data.headline,
              subheadline: data.subheadline
            });
            setTestId(data.test_id);
            setVariantId(data.variant_id);
            
            // Track impression
            trackImpression(data.test_id, data.variant_id);
            
            // Store in sessionStorage to maintain consistency during session
            sessionStorage.setItem(`ab_${page}_${element}`, JSON.stringify(data));
          }
        }
      } catch (error) {
        console.log('A/B test fetch failed, using default content');
      } finally {
        setLoading(false);
      }
    };

    // Check sessionStorage first for consistency
    const cached = sessionStorage.getItem(`ab_${page}_${element}`);
    if (cached) {
      try {
        const data = JSON.parse(cached);
        setVariant({
          headline: data.headline,
          subheadline: data.subheadline
        });
        setTestId(data.test_id);
        setVariantId(data.variant_id);
        setLoading(false);
        return;
      } catch {
        // Continue to fetch
      }
    }

    fetchVariant();
  }, [page, element]);

  // Track impression
  const trackImpression = async (tId, vId) => {
    try {
      await fetch(`${API_URL}/api/ab-test/impression?test_id=${tId}&variant_id=${vId}`, {
        method: 'POST'
      });
    } catch (error) {
      console.log('Failed to track impression');
    }
  };

  // Track conversion (click)
  const trackConversion = useCallback(async (conversionType = 'click') => {
    if (!testId || !variantId) return;
    
    try {
      await fetch(
        `${API_URL}/api/ab-test/conversion?test_id=${testId}&variant_id=${variantId}&conversion_type=${conversionType}`,
        { method: 'POST' }
      );
    } catch (error) {
      console.log('Failed to track conversion');
    }
  }, [testId, variantId]);

  return {
    variant,
    loading,
    trackConversion,
    isInTest: !!testId
  };
};

export default useABTest;
